import { Component, type ReactNode } from "react"
import { useRouteError } from "react-router"
import { ErrorState } from "@/components/kit/states"
import { useReplaceBootScreen } from "@/lib/boot-screen"

/**
 * FR-DSH-SHL-07: a recoverable error state with the requestId and a retry.
 * Never a blank screen, never a raw stack in front of the client.
 */
export function RouteErrorBoundary() {
  useReplaceBootScreen()
  const error = useRouteError()
  return (
    <ErrorState
      error={error}
      onRetry={() => {
        window.location.reload()
      }}
    />
  )
}

interface Props {
  children: ReactNode
  fallback?: (error: unknown, reset: () => void) => ReactNode
}
interface State {
  error: unknown
}

/**
 * Catches render-time failures below it. Used to keep one broken region from
 * blanking a whole screen -- PDD §11.2 state 4, "a failed sub-resource degrades
 * its region, not the screen".
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: unknown): State {
    return { error }
  }

  // No componentDidCatch logging: React 19 already reports a caught render
  // error (root `onCaughtError`), and `no-console` holds (NFR-DSH-15).

  private readonly reset = () => {
    this.setState({ error: null })
  }

  override render(): ReactNode {
    if (this.state.error !== null) {
      return this.props.fallback ? (
        this.props.fallback(this.state.error, this.reset)
      ) : (
        <ErrorState error={this.state.error} onRetry={this.reset} />
      )
    }
    return this.props.children
  }
}
